import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Clock, ChefHat, Bell, PackageCheck, XCircle, Loader2, MapPin, Store, Truck, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: 'pending', label: 'Saņemts', icon: Clock },
  { key: 'confirmed', label: 'Pieņemts', icon: ChefHat },
  { key: 'ready', label: 'Gatavs', icon: Bell },
  { key: 'completed', label: 'Izsniegts', icon: PackageCheck },
];
const ORDER = { pending: 0, confirmed: 1, ready: 2, completed: 3 };

export default function OrderStatusPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/business-page/order/${encodeURIComponent(code)}`);
      setData(d); setNotFound(false);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally { setLoading(false); }
  }, [code]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const cancelled = data && (data.status === 'rejected' || data.status === 'cancelled');
  const activeIdx = data ? (ORDER[data.status] ?? 0) : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col items-center px-4 py-10" data-testid="order-status-page">
      <Link to="/" className="mb-8 text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>
        Ko<span className="text-amber-400">LV</span>
      </Link>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 mt-20" data-testid="order-loading"><Loader2 className="w-5 h-5 animate-spin" /> Ielādē…</div>
      ) : notFound ? (
        <div className="text-center mt-16 max-w-sm" data-testid="order-notfound">
          <XCircle className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-1">Pasūtījums nav atrasts</h1>
          <p className="text-white/50 text-sm">Pārbaudi, vai kods <span className="font-mono text-amber-400">{code}</span> ir pareizs.</p>
        </div>
      ) : (
        <div className="w-full max-w-md" data-testid="order-status-card">
          <div className="text-center mb-8">
            <p className="text-white/40 text-xs uppercase tracking-wide">Pasūtījums</p>
            <p className="font-mono text-2xl font-bold text-amber-400" data-testid="order-code">{data.code}</p>
            {data.business_name && (
              <Link to={`/uznemums/${data.business_user_id}${data.page_id ? `/${data.page_id}` : ''}`} className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mt-1">
                {data.business_name} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>

          {cancelled ? (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-center mb-6" data-testid="order-cancelled">
              <XCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              <p className="font-semibold">Pasūtījums atcelts</p>
              <p className="text-white/50 text-sm mt-1">Lūdzu sazinies ar uzņēmumu.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 mb-6">
              <div className="space-y-0">
                {STEPS.map((s, i) => {
                  const isCompleted = data.status === 'completed';
                  const done = i < activeIdx || (i === activeIdx && isCompleted);
                  const current = i === activeIdx && !isCompleted;
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className="flex items-start gap-4" data-testid={`order-step-${s.key}`} data-state={done ? 'done' : current ? 'current' : 'todo'}>
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${done ? 'bg-emerald-500 text-white' : current ? 'bg-amber-400 text-black' : 'bg-white/10 text-white/40'}`}>
                          {done ? <Check className="w-5 h-5" /> : <Icon className={`w-5 h-5 ${current ? 'animate-pulse' : ''}`} />}
                        </div>
                        {i < STEPS.length - 1 && <div className={`w-0.5 h-8 ${(i < activeIdx || isCompleted) ? 'bg-emerald-500' : 'bg-white/10'}`} />}
                      </div>
                      <div className="pt-2">
                        <p className={`font-semibold ${current ? 'text-amber-400' : done ? 'text-white' : 'text-white/40'}`}>{s.label}</p>
                        {current && <p className="text-white/40 text-xs">{s.key === 'pending' ? 'Gaida apstiprinājumu' : s.key === 'confirmed' ? 'Tiek gatavots' : s.key === 'ready' ? (data.fulfillment === 'delivery' ? 'Drīz piegādāsim' : 'Vari saņemt uz vietas') : 'Izsniegts — paldies!'}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm space-y-3" data-testid="order-details">
            <div className="flex items-center gap-2 text-white/70">
              {data.fulfillment === 'delivery' ? <Truck className="w-4 h-4 text-amber-400" /> : <Store className="w-4 h-4 text-amber-400" />}
              {data.fulfillment === 'delivery' ? 'Piegāde' : 'Saņemšana uz vietas'}
              {data.requested_time ? <span className="text-white/40">· {data.requested_time}</span> : null}
            </div>
            {data.address && <div className="flex items-center gap-2 text-white/50"><MapPin className="w-4 h-4" /> {data.address}</div>}
            {data.cart?.length > 0 && (
              <div className="border-t border-white/10 pt-3 space-y-1.5">
                {data.cart.map((l, i) => (
                  <div key={i} className="flex justify-between gap-3" data-testid={`order-item-${i}`}>
                    <span className="text-white/70">{l.qty}× {l.name}{l.options?.length ? <span className="text-white/35"> ({l.options.join(', ')})</span> : null}</span>
                    <span className="text-white/50 shrink-0">€{Number(l.line_total || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            {data.amount != null && (
              <div className="border-t border-white/10 pt-3 flex justify-between font-semibold">
                <span>Kopā {data.pay_choice === 'external' ? '(maksā saņemot)' : data.payment_status === 'paid' ? '(apmaksāts)' : ''}</span>
                <span className="text-amber-400">€{Number(data.amount || 0).toFixed(2)}</span>
              </div>
            )}
          </div>

          <p className="text-center text-white/30 text-xs mt-6">Statuss atjaunojas automātiski.</p>
        </div>
      )}
    </div>
  );
}
